import {
  assign,
  fromCallback,
  fromPromise,
  type SnapshotFrom,
  setup,
} from "xstate";

import { toErrorMessage } from "@src/shared/utils/errors";

import { type SignUpStepId } from "../../auth.schemas";
import {
  EMAIL_VERIFICATION_POLL_INTERVAL_MS,
  PROVISIONING_MESSAGE_FINALIZING_MS,
  PROVISIONING_MESSAGE_SETTING_UP_MS,
  PROVISIONING_MESSAGE_SLOW_MS,
  PROVISIONING_PENDING_TIMEOUT_MS,
  PROVISIONING_POLL_INTERVAL_MS,
} from "./auth.provisioning.utils";

export type SignUpMachineAccountValues = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
};

export type SignUpAccountCreationResult = {
  email: string;
};

export type ProvisioningStatus = {
  state: "pending" | "succeeded" | "failed";
  failureReason?: { code: string; message: string } | null;
};

/**
 * Normalized outcome of one provisioning-status probe. The application layer
 * owns the fetch + error classification (network vs 404 vs unexpected); the
 * machine only decides what each outcome means for the lifecycle.
 */
export type ProvisioningPollResult =
  | { kind: "status"; status: ProvisioningStatus }
  | { kind: "not-found" }
  | { kind: "unauthorized" }
  | { kind: "network-error" }
  | { kind: "unexpected-error"; requestId?: string };

export type EmailVerificationResult = {
  verified: boolean;
  email: string | null;
};

/**
 * IO the machine orchestrates. Every function talks to the network/browser;
 * the machine keeps none of that knowledge itself, so it stays unit-testable
 * by stubbing these.
 */
export type SignUpProvisioningApi = {
  /**
   * `invitationId` only tells the server which practice to join; the account is
   * created exactly as it is for a normal sign-up.
   */
  createAccount: (
    values: SignUpMachineAccountValues & { invitationId: string | null },
  ) => Promise<SignUpAccountCreationResult>;
  /**
   * Consumes the invitation. This is the invitee's final action, once the
   * account exists and they are signed in.
   */
  acceptInvitation: (invitationId: string) => Promise<void>;
  completeSignup: () => Promise<void>;
  fetchProvisioningStatus: () => Promise<ProvisioningPollResult>;
  checkEmailVerification: () => Promise<EmailVerificationResult>;
  readVerificationSession: () => Promise<EmailVerificationResult>;
  resendVerificationEmail: (email: string | null) => Promise<void>;
};

/**
 * Machine construction args: the IO contract plus the step to start on, so a
 * deep link / refresh resumes at the right place (the URL seeds the machine
 * once; from then on the machine owns the step and the URL merely reflects it).
 */
export type SignUpProvisioningMachineInput = SignUpProvisioningApi & {
  initialStep: SignUpStepId;
  invitationId: string | null;
};

export type SignUpProvisioningMachineContext = SignUpProvisioningApi & {
  activeStep: SignUpStepId;
  invitationId: string | null;
  invitationAcceptError: string | null;
  accountError: string | null;
  emailVerificationError: string | null;
  verificationEmail: string | null;
  provisioningStatus: ProvisioningStatus | null;
  unexpectedErrorReference: string | null;
};

export type SignUpProvisioningMachineEvent =
  | { type: "SUBMIT_ACCOUNT"; values: SignUpMachineAccountValues }
  | { type: "SET_STEP"; step: SignUpStepId }
  | { type: "PROVISIONING_STATUS"; status: ProvisioningStatus }
  | { type: "PROVISIONING_UNEXPECTED_ERROR"; requestId?: string }
  | { type: "PROVISIONING_NOT_FOUND" }
  | { type: "PROVISIONING_UNAUTHORIZED" }
  | { type: "CHECK_VERIFICATION" }
  | { type: "RESEND_EMAIL" }
  | { type: "VERIFICATION_EMAIL"; email: string }
  | { type: "EMAIL_VERIFIED" }
  | { type: "FINISH" };

export type SignUpProvisioningSnapshot = SnapshotFrom<
  typeof signUpProvisioningMachine
>;

function getAccountCreationResult(
  event: unknown,
): SignUpAccountCreationResult | null {
  if (!event || typeof event !== "object" || !("output" in event)) {
    return null;
  }

  const output = (event as { output: unknown }).output;

  if (!output || typeof output !== "object") return null;

  const result = output as Partial<SignUpAccountCreationResult>;

  return typeof result.email === "string" ? { email: result.email } : null;
}

function getEmailVerificationResult(
  event: unknown,
): EmailVerificationResult | null {
  if (!event || typeof event !== "object" || !("output" in event)) {
    return null;
  }

  const output = (event as { output: unknown }).output;

  if (!output || typeof output !== "object") return null;

  const result = output as Partial<EmailVerificationResult>;

  return typeof result.verified === "boolean"
    ? { verified: result.verified, email: result.email ?? null }
    : null;
}

export const signUpProvisioningMachine = setup({
  types: {
    context: {} as SignUpProvisioningMachineContext,
    events: {} as SignUpProvisioningMachineEvent,
    input: {} as SignUpProvisioningMachineInput,
  },
  actors: {
    createAccount: fromPromise(
      async ({
        input,
      }: {
        input: {
          createAccount: SignUpProvisioningMachineInput["createAccount"];
          invitationId: string | null;
          values: SignUpMachineAccountValues;
        };
      }) => {
        return input.createAccount({
          ...input.values,
          invitationId: input.invitationId,
        });
      },
    ),
    acceptInvitation: fromPromise(
      async ({
        input,
      }: {
        input: {
          acceptInvitation: SignUpProvisioningMachineInput["acceptInvitation"];
          invitationId: string;
        };
      }) => {
        await input.acceptInvitation(input.invitationId);
      },
    ),
    completeSignup: fromPromise(
      async ({
        input,
      }: {
        input: Pick<SignUpProvisioningMachineInput, "completeSignup">;
      }) => {
        await input.completeSignup();
      },
    ),
    checkEmailVerification: fromPromise(
      async ({
        input,
      }: {
        input: Pick<SignUpProvisioningMachineInput, "checkEmailVerification">;
      }) => {
        return input.checkEmailVerification();
      },
    ),
    resendEmail: fromPromise(
      async ({
        input,
      }: {
        input: {
          resendVerificationEmail: SignUpProvisioningMachineInput["resendVerificationEmail"];
          email: string | null;
        };
      }) => {
        await input.resendVerificationEmail(input.email);
      },
    ),
    pollProvisioningStatus: fromCallback<
      SignUpProvisioningMachineEvent,
      Pick<SignUpProvisioningMachineInput, "fetchProvisioningStatus">
    >(({ input, sendBack }) => {
      let cancelled = false;

      const tick = async () => {
        const result = await input.fetchProvisioningStatus();

        if (cancelled) return;

        switch (result.kind) {
          case "status":
            sendBack({ type: "PROVISIONING_STATUS", status: result.status });
            break;
          case "not-found":
            sendBack({ type: "PROVISIONING_NOT_FOUND" });
            break;
          case "unauthorized":
            sendBack({ type: "PROVISIONING_UNAUTHORIZED" });
            break;
          case "unexpected-error":
            sendBack({
              type: "PROVISIONING_UNEXPECTED_ERROR",
              requestId: result.requestId,
            });
            break;
          case "network-error":
            break;
        }
      };

      void tick();
      const intervalId = setInterval(() => {
        void tick();
      }, PROVISIONING_POLL_INTERVAL_MS);

      return () => {
        cancelled = true;
        clearInterval(intervalId);
      };
    }),
    pollEmailVerification: fromCallback<
      SignUpProvisioningMachineEvent,
      Pick<SignUpProvisioningMachineInput, "readVerificationSession">
    >(({ input, sendBack }) => {
      let cancelled = false;

      const tick = async () => {
        try {
          const result = await input.readVerificationSession();

          if (cancelled) return;

          if (result.email) {
            sendBack({ type: "VERIFICATION_EMAIL", email: result.email });
          }

          if (result.verified) {
            sendBack({ type: "EMAIL_VERIFIED" });
          }
        } catch {
          // A failed poll is treated as "not verified yet" — keep polling.
        }
      };

      void tick();
      const intervalId = setInterval(() => {
        void tick();
      }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);

      return () => {
        cancelled = true;
        clearInterval(intervalId);
      };
    }),
  },
  actions: {
    clearAccountError: assign({
      accountError: null,
      unexpectedErrorReference: null,
    }),
    setAccountError: assign({
      accountError: ({ event }) =>
        "error" in event
          ? toErrorMessage(event.error, "Unable to create account.")
          : "Unable to create account.",
    }),
    resetProvisioning: assign({
      accountError: null,
      provisioningStatus: null,
      unexpectedErrorReference: null,
    }),
    resumeProvisioning: assign({
      accountError: null,
      unexpectedErrorReference: null,
    }),
    setProvisioningStatus: assign({
      provisioningStatus: ({ event }) =>
        event.type === "PROVISIONING_STATUS" ? event.status : null,
      unexpectedErrorReference: null,
    }),
    setVerificationEmailFromAccount: assign({
      verificationEmail: ({ context, event }) =>
        getAccountCreationResult(event)?.email ?? context.verificationEmail,
    }),
    setVerificationEmailFromResult: assign({
      verificationEmail: ({ context, event }) =>
        getEmailVerificationResult(event)?.email ?? context.verificationEmail,
    }),
    setVerificationEmailFromEvent: assign({
      verificationEmail: ({ context, event }) =>
        event.type === "VERIFICATION_EMAIL"
          ? event.email
          : context.verificationEmail,
    }),
    setUnexpectedErrorReference: assign({
      unexpectedErrorReference: ({ event }) =>
        event.type === "PROVISIONING_UNEXPECTED_ERROR"
          ? (event.requestId ?? null)
          : null,
    }),
    clearInvitationAcceptError: assign({ invitationAcceptError: null }),
    setInvitationAcceptError: assign({
      invitationAcceptError: ({ event }) =>
        "error" in event
          ? toErrorMessage(event.error, "Unable to accept the invitation.")
          : "Unable to accept the invitation.",
    }),
    clearEmailVerificationError: assign({ emailVerificationError: null }),
    setEmailNotVerifiedError: assign({
      emailVerificationError:
        "We have not detected a verified session yet. Use the link in your email, then try again.",
    }),
    setEmailCheckError: assign({
      emailVerificationError:
        "We could not check your verification status. Please try again.",
    }),
    setResendError: assign({
      emailVerificationError: ({ event }) =>
        "error" in event
          ? toErrorMessage(event.error, "Unable to resend verification email.")
          : "Unable to resend verification email.",
    }),
    setActiveStep: assign({
      activeStep: ({ context, event }) =>
        event.type === "SET_STEP" ? event.step : context.activeStep,
    }),
    advanceToMicrophone: assign({ activeStep: "microphone" as SignUpStepId }),
    goToVerifyEmailStep: assign({
      activeStep: "verify-email" as SignUpStepId,
    }),
  },
  guards: {
    provisioningSucceeded: ({ event }) =>
      event.type === "PROVISIONING_STATUS" &&
      event.status.state === "succeeded",
    provisioningFailed: ({ event }) =>
      event.type === "PROVISIONING_STATUS" && event.status.state === "failed",
    emailVerified: ({ event }) =>
      getEmailVerificationResult(event)?.verified === true,
    startsOnProfile: ({ context }) => context.activeStep === "profile",
    onVerifyEmailStep: ({ context }) => context.activeStep === "verify-email",
    onFinalStepAsInvitee: ({ context }) =>
      context.activeStep === "verify-email" && context.invitationId !== null,
    onFinalStepAsSignUp: ({ context }) =>
      context.activeStep === "verify-email" && context.invitationId === null,
  },
  delays: {
    provisioningTimeout: PROVISIONING_PENDING_TIMEOUT_MS,
    messageSettingUp: PROVISIONING_MESSAGE_SETTING_UP_MS,
    messageFinalizing: PROVISIONING_MESSAGE_FINALIZING_MS,
    messageSlow: PROVISIONING_MESSAGE_SLOW_MS,
  },
}).createMachine({
  id: "signUpProvisioning",
  context: ({ input }) => {
    const { initialStep, invitationId, ...api } = input;

    return {
      ...api,
      activeStep: initialStep,
      invitationId,
      invitationAcceptError: null,
      accountError: null,
      emailVerificationError: null,
      verificationEmail: null,
      provisioningStatus: null,
      unexpectedErrorReference: null,
    };
  },
  initial: "init",
  // User-driven wizard navigation is orthogonal to the provisioning lifecycle,
  // so it's handled at the root and simply records the step in context.
  on: {
    SET_STEP: {
      actions: "setActiveStep",
    },
  },
  states: {
    // Seeds the lifecycle from the step the URL deep-linked to: a refresh at a
    // post-profile step resumes provisioning instead of starting over.
    init: {
      always: [
        { guard: "startsOnProfile", target: "idle" },
        { guard: "onFinalStepAsInvitee", target: "invitationComplete" },
        { guard: "onFinalStepAsSignUp", target: "emailVerification" },
        { target: "provisioning", actions: "resumeProvisioning" },
      ],
    },
    idle: {
      on: {
        SUBMIT_ACCOUNT: {
          target: "creatingAccount",
          actions: "clearAccountError",
        },
      },
    },
    creatingAccount: {
      invoke: {
        src: "createAccount",
        input: ({ context, event }) => {
          if (event.type !== "SUBMIT_ACCOUNT") {
            throw new Error("Missing account values for sign-up.");
          }

          return {
            createAccount: context.createAccount,
            invitationId: context.invitationId,
            values: event.values,
          };
        },
        onDone: {
          target: "provisioning",
          actions: [
            "resetProvisioning",
            "setVerificationEmailFromAccount",
            "advanceToMicrophone",
          ],
        },
        onError: {
          target: "accountCreationFailed",
          actions: "setAccountError",
        },
      },
    },
    accountCreationFailed: {
      on: {
        SUBMIT_ACCOUNT: {
          target: "creatingAccount",
          actions: "clearAccountError",
        },
      },
    },
    provisioning: {
      initial: "pending",
      states: {
        pending: {
          initial: "creating",
          invoke: {
            src: "pollProvisioningStatus",
            input: ({ context }) => ({
              fetchProvisioningStatus: context.fetchProvisioningStatus,
            }),
          },
          after: {
            provisioningTimeout: {
              target: "#signUpProvisioning.provisioning.timedOut",
            },
          },
          on: {
            PROVISIONING_STATUS: [
              {
                guard: "provisioningSucceeded",
                target: "completing",
                actions: "setProvisioningStatus",
              },
              {
                guard: "provisioningFailed",
                target: "failed",
                actions: "setProvisioningStatus",
              },
              {
                actions: "setProvisioningStatus",
              },
            ],
            PROVISIONING_NOT_FOUND: {
              target: "failed",
            },
            PROVISIONING_UNAUTHORIZED: {
              target: "unauthorized",
            },
            PROVISIONING_UNEXPECTED_ERROR: {
              target: "failed",
              actions: "setUnexpectedErrorReference",
            },
          },
          states: {
            creating: {
              after: { messageSettingUp: "settingUp" },
            },
            settingUp: {
              after: { messageFinalizing: "finalizing" },
            },
            finalizing: {
              after: { messageSlow: "slow" },
            },
            slow: {},
          },
        },
        completing: {
          invoke: {
            src: "completeSignup",
            input: ({ context }) => ({
              completeSignup: context.completeSignup,
            }),
            onDone: {
              target: "succeeded",
            },
            onError: {
              target: "failed",
              actions: "setUnexpectedErrorReference",
            },
          },
        },
        succeeded: {
          // Entering the final step is driven purely by the step: advancing to
          // verify-email (SET_STEP from the trust gate, or a refresh that
          // deep-links straight here) flips one of these guards. Invitees go to
          // a plain confirmation because their email is already verified.
          always: [
            {
              guard: "onFinalStepAsInvitee",
              target: "#signUpProvisioning.invitationComplete",
            },
            {
              guard: "onFinalStepAsSignUp",
              target: "#signUpProvisioning.emailVerification",
            },
          ],
        },
        failed: {},
        timedOut: {},
        unauthorized: {},
      },
    },
    // Invitees arrive here already provisioned and already verified, so the
    // only thing left is to consume the invitation.
    invitationComplete: {
      initial: "ready",
      entry: "goToVerifyEmailStep",
      states: {
        ready: {
          on: {
            FINISH: { target: "accepting" },
          },
        },
        accepting: {
          entry: "clearInvitationAcceptError",
          invoke: {
            src: "acceptInvitation",
            input: ({ context }) => {
              if (!context.invitationId) {
                throw new Error("Missing invitation to accept.");
              }

              return {
                acceptInvitation: context.acceptInvitation,
                invitationId: context.invitationId,
              };
            },
            onDone: {
              target: "#signUpProvisioning.done",
            },
            onError: {
              target: "ready",
              actions: "setInvitationAcceptError",
            },
          },
        },
      },
    },
    emailVerification: {
      initial: "pending",
      entry: "goToVerifyEmailStep",
      states: {
        pending: {
          invoke: {
            src: "pollEmailVerification",
            input: ({ context }) => ({
              readVerificationSession: context.readVerificationSession,
            }),
          },
          on: {
            VERIFICATION_EMAIL: {
              actions: "setVerificationEmailFromEvent",
            },
            EMAIL_VERIFIED: {
              target: "#signUpProvisioning.done",
            },
            CHECK_VERIFICATION: {
              target: "checking",
            },
            RESEND_EMAIL: {
              target: "resending",
            },
          },
        },
        checking: {
          entry: "clearEmailVerificationError",
          invoke: {
            src: "checkEmailVerification",
            input: ({ context }) => ({
              checkEmailVerification: context.checkEmailVerification,
            }),
            onDone: [
              {
                guard: "emailVerified",
                target: "#signUpProvisioning.done",
                actions: "setVerificationEmailFromResult",
              },
              {
                target: "pending",
                actions: [
                  "setVerificationEmailFromResult",
                  "setEmailNotVerifiedError",
                ],
              },
            ],
            onError: {
              target: "pending",
              actions: "setEmailCheckError",
            },
          },
        },
        resending: {
          entry: "clearEmailVerificationError",
          invoke: {
            src: "resendEmail",
            input: ({ context }) => ({
              resendVerificationEmail: context.resendVerificationEmail,
              email: context.verificationEmail,
            }),
            onDone: {
              target: "pending",
            },
            onError: {
              target: "pending",
              actions: "setResendError",
            },
          },
        },
      },
    },
    done: {
      type: "final",
    },
  },
});
